/* Illustrative figures for the overview's no-agents backdrop (OPSS-261).
 *
 * A workspace with no agents has nothing to read, so the dashboard ghosted
 * behind the empty-state overlay rendered zeros and loading skeletons — it read
 * as broken rather than as a preview of what connecting an agent buys you. The
 * overlay shows these instead. They are display-only: nothing here reaches a
 * query, a cache, or an event, and the moment one agent reports traffic the page
 * is back on live data.
 *
 * Kept plausible rather than flattering (a believable spend, a 38% saving) and
 * fully deterministic — no clock, no randomness — so the backdrop never flickers
 * between renders. */

import type {
  AgentRollup,
  ModelRollup,
  Summary,
  TrendPoint,
} from "../../analytics";

const EMPTY_TOKENS = {
  input: 0,
  output: 0,
  cache_read: 0,
  cache_write: 0,
} as const;

const EMPTY_BYTES = { before: 0, after: 0, saved: 0, saved_pct: 0 } as const;

/* The cards below read none of these; they exist because the wire type does. */
const EMPTY_TRIPLE = { avg: 0, p50: 0, p95: 0 } as const;
const EMPTY_LATENCY = {
  gateway: EMPTY_TRIPLE,
  pipeline: EMPTY_TRIPLE,
  upstream: EMPTY_TRIPLE,
  stream_total: EMPTY_TRIPLE,
} as const;

/* Savings are quoted against gross spend (net paid + saved), the one definition
 * savedPct uses: 760 / (1240 + 760) = 38%. */
const DEMO_NET_SPEND = 1240;
const DEMO_SAVED = 760;

export const DEMO_SUMMARY: Summary = {
  call_count: 28_400,
  session_count: 1_180,
  failed_calls: 0,
  error_rate: 0,
  cost: {
    input: 236,
    cache_read: 402,
    cache_write: 448,
    output: 154,
    total: DEMO_NET_SPEND,
  },
  tokens: {
    input: 41_200_000,
    output: 3_180_000,
    cache_read: 96_400_000,
    cache_write: 27_600_000,
  },
  bytes: EMPTY_BYTES,
  tokens_est: EMPTY_BYTES,
  cache: {
    read_tokens: 96_400_000,
    write_tokens: 27_600_000,
    write_5m_tokens: 0,
    write_1h_tokens: 0,
    invalidations: 0,
  },
  models: [
    { name: "claude-sonnet-4-5", calls: 19_900, cost_usd: 809 },
    { name: "claude-haiku-4-5", calls: 6_100, cost_usd: 268 },
    { name: "gpt-5.1-codex", calls: 2_400, cost_usd: 163 },
  ],
  routing: { routed_calls: 0, routed_pct: 0, by_target: [] },
  latency_ms: EMPTY_LATENCY,
  savings_usd: DEMO_SAVED,
  context_blocks: [],
};

/** Three agents in the roster — named for their integration, so the row reads as
 *  an example and not as somebody else's workspace. */
export const DEMO_AGENTS: AgentRollup[] = [
  {
    agent_id: "claude-code",
    agent_kind: "claude_code",
    name: "Claude Code",
    call_count: 17_300,
    failed_calls: 0,
    session_count: 690,
    cost: {
      input: 142,
      cache_read: 249,
      cache_write: 271,
      output: 92,
      total: 754,
    },
    tokens: {
      input: 25_100_000,
      output: 1_940_000,
      cache_read: 58_800_000,
      cache_write: 16_800_000,
    },
    bytes_saved_pct: 41.2,
    savings_usd: 502,
    routing: { routed_calls: 0 },
    models: [{ name: "claude-sonnet-4-5", calls: 17_300 }],
  },
  {
    agent_id: "codex",
    agent_kind: "codex",
    name: "Codex",
    call_count: 7_600,
    failed_calls: 0,
    session_count: 330,
    cost: {
      input: 63,
      cache_read: 107,
      cache_write: 120,
      output: 41,
      total: 331,
    },
    tokens: {
      input: 11_000_000,
      output: 850_000,
      cache_read: 25_700_000,
      cache_write: 7_400_000,
    },
    bytes_saved_pct: 33.8,
    savings_usd: 178,
    routing: { routed_calls: 0 },
    models: [{ name: "gpt-5.1-codex", calls: 7_600 }],
  },
  {
    agent_id: "langchain-service",
    agent_kind: "langchain",
    name: "LangChain service",
    call_count: 3_500,
    failed_calls: 0,
    session_count: 160,
    cost: {
      input: 31,
      cache_read: 46,
      cache_write: 57,
      output: 21,
      total: 155,
    },
    tokens: {
      input: 5_100_000,
      output: 390_000,
      cache_read: 11_900_000,
      cache_write: 3_400_000,
    },
    bytes_saved_pct: 27.5,
    savings_usd: 80,
    routing: { routed_calls: 0 },
    models: [{ name: "claude-haiku-4-5", calls: 3_500 }],
  },
];

/* Behind the empty-state overlay the Models section needs figures too, or it
 * renders "No model usage in this range." through the scrim. Derived from the
 * same DEMO_AGENTS so the two ghosted breakdowns agree with each other and with
 * the spend tile — the sum a reader's eye lands on first. */
export const DEMO_MODELS: ModelRollup[] = (() => {
  const byModel = new Map<string, ModelRollup>();
  for (const agent of DEMO_AGENTS) {
    for (const m of agent.models) {
      // The agent's `models` array already partitions THAT AGENT's totals, so the
      // weight is the model's share of the agent — not the agent's share of the
      // window. Weighting by the latter scaled every row down by the agent's own
      // share and the rows summed to ~46% of the spend tile they sit under, which
      // is the disagreement this block exists to avoid.
      const share = m.calls / (agent.call_count || 1);
      const row = byModel.get(m.name) ?? {
        model: m.name,
        call_count: 0,
        failed_calls: 0,
        cost: { input: 0, cache_read: 0, cache_write: 0, output: 0, total: 0 },
        tokens: { input: 0, output: 0, cache_read: 0, cache_write: 0 },
        bytes_saved_pct: agent.bytes_saved_pct,
        savings_usd: 0,
      };
      row.call_count += m.calls;
      row.cost.input += agent.cost.input * share;
      row.cost.cache_read += agent.cost.cache_read * share;
      row.cost.cache_write += agent.cost.cache_write * share;
      row.cost.output += agent.cost.output * share;
      row.cost.total += agent.cost.total * share;
      row.tokens.input += agent.tokens.input * share;
      row.tokens.output += agent.tokens.output * share;
      row.tokens.cache_read += agent.tokens.cache_read * share;
      row.tokens.cache_write += agent.tokens.cache_write * share;
      row.savings_usd += agent.savings_usd * share;
      byModel.set(m.name, row);
    }
  }
  return [...byModel.values()].sort((a, b) => b.cost.total - a.cost.total);
})();

/** Per-bucket share of the window's spend. Cycled, so the trend line has a
 *  believable shape at any range length without a clock or a PRNG. */
const SHAPE = [0.72, 0.85, 0.64, 1, 0.91, 0.78, 0.96, 0.7, 0.88, 0.81];

/** Demo trend points spanning the active range, so the chart's axis labels still
 *  match the timeframe the reader has selected. */
export function demoTrendPoints(
  from: string,
  to: string,
  granularity: "hour" | "day",
): TrendPoint[] {
  const step = granularity === "hour" ? 3_600_000 : 86_400_000;
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return [];
  }

  const count = Math.max(1, Math.min(90, Math.round((end - start) / step)));
  // Spread the window's spend across the buckets in the SHAPE proportions.
  const weightTotal = Array.from(
    { length: count },
    (_, i) => SHAPE[i % SHAPE.length],
  ).reduce((a, b) => a + b, 0);

  return Array.from({ length: count }, (_, i) => {
    const share = SHAPE[i % SHAPE.length] / weightTotal;
    const total = DEMO_SUMMARY.cost.total * share;
    return {
      bucket: new Date(start + i * step).toISOString(),
      call_count: Math.round(DEMO_SUMMARY.call_count * share),
      cost: {
        input: DEMO_SUMMARY.cost.input * share,
        cache_read: DEMO_SUMMARY.cost.cache_read * share,
        cache_write: DEMO_SUMMARY.cost.cache_write * share,
        output: DEMO_SUMMARY.cost.output * share,
        total,
      },
      tokens: EMPTY_TOKENS,
    };
  });
}
